/**
 * What a NIR looks like once its spaces are gone: 13 characters — the kind of
 * number, then year, month, department (2A/2B for Corsica), commune, order —
 * optionally followed by its 2-character key.
 *
 * The first character says the kind: 1 or 2, a registered man or woman; 3 or
 * 4, someone born abroad whose registration is under way; 7 or 8, a
 * provisional number. All of them are numbers the Assurance maladie gave, and
 * all of them go to the portal: it is the one to say whether it knows them.
 */
const NIR = /^[123478]\d{4}(?:\d{2}|2A|2B)\d{6}(\d{2})?$/i;

/**
 * Whether a NIR is a temporary one — a registration under way, a provisional
 * number — rather than a settled registration.
 *
 * @param value The NIR, spaces removed.
 */
export const isTemporaryNir = (value: string): boolean => /^[3478]/.test(value);

/**
 * Whether a 15-character NIR carries the right key.
 *
 * The key is 97 minus the number modulo 97, the Corsican department letters
 * standing for 19 (2A) and 18 (2B) in that computation.
 *
 * @param compact The 15 characters, spaces already removed.
 */
const hasValidKey = (compact: string): boolean => {
    const number = compact.slice(0, 13).toUpperCase().replace('2A', '19').replace('2B', '18');
    const key = Number(compact.slice(13));

    return 97 - (Number(number) % 97) === key;
};

/**
 * The NIR as the amelipro patient lookup wants it: the 13 characters of the
 * number itself, without the key, as a string — or nothing at all.
 *
 * Detections hand the NIR over in more than one shape — a string from the
 * Vitale read, a number once a checksum has been through it, 15 characters
 * with the key, spaces from a typed field — and sometimes something that is
 * not a NIR: a consumer key, a partial value. The portal takes 13 characters
 * and answers a generic 500 to anything else, which tells the pharmacist
 * nothing. So what is not a NIR is refused here, before the portal, and said
 * as such. The key is checked on settled registrations only: a temporary
 * number's is not ours to judge, and the portal answers for it.
 *
 * @param value The NIR as it came, whatever it is.
 * @returns The 13 characters, or an empty string when the value is not a NIR.
 */
export const portalNir = (value: unknown): string => {
    if (value === null || value === undefined) {
        return '';
    }

    const compact = String(value).replace(/\s/g, '');

    if (!NIR.test(compact)) {
        return '';
    }

    if (compact.length === 15) {
        return isTemporaryNir(compact) || hasValidKey(compact) ? compact.slice(0, 13) : '';
    }

    return compact;
};

/**
 * The shape of a value, digits as `d` and letters as `a`, for a trace that
 * must never carry the value itself.
 *
 * @param value Whatever came in.
 */
export const nirShape = (value: unknown): string =>
    String(value ?? '').replace(/[0-9A-Za-z]/g, character => (/\d/.test(character) ? 'd' : 'a'));
